"use client"

import {useCallback, useEffect, useRef} from "react";
import {useAppSelector} from "@/lib/hooks";
import {
    useGetContactVerifyStateQuery,
    useMarkRemindLaterMutation,
} from "@/lib/api/auth/contactVerifyApi";
import {usePathname} from "@/i18n/navigation";
import {useRequestDialog} from "@/lib/dialog-orchestrator/react";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {MAX_TIMEOUT_DELAY} from "@/lib/utils/contants/timers";

/**
 * Headless controller: decides *when* the soft contact-verify modal should show
 * and requests it from the orchestrator. The dialog itself is rendered by
 * DialogHost. "Remind later" returns as a dismissal carrying {remindLater: true}
 * (see the CONTACT_VERIFY adapter), so we fire the mutation off the result.
 */
export default function ContactVerifyProvider() {
    const userId = useAppSelector(state => state.auth.userId);
    const pathname = usePathname();
    const isGamePage = pathname.includes('/games/');
    const shownRef = useRef(false);
    const [markRemindLater] = useMarkRemindLaterMutation();
    const requestDialog = useRequestDialog();

    const {data: state, refetch} = useGetContactVerifyStateQuery(undefined, {
        skip: !userId || isGamePage,
    });

    const openContactVerify = useCallback(() => {
        if (!state || state.requiredContactVerified) return;
        requestDialog(DialogsEnum.CONTACT_VERIFY, {props: {mode: 'soft', state}})
            .then(async ({data}) => {
                if ((data as {remindLater?: boolean} | undefined)?.remindLater) {
                    await markRemindLater().unwrap().catch(() => {});
                }
            });
    }, [state, requestDialog, markRemindLater]);

    useEffect(() => {
        if (!state || state.requiredContactVerified) return;
        if (!state.softModalRequired || isGamePage) return;

        if (state.nextContactReminderAt) {
            const delay = new Date(state.nextContactReminderAt).getTime() - Date.now();
            if (delay > 0) {
                // The timer must not outlive this effect pass: once `isGamePage`
                // flips the query is unsubscribed and refetch() throws (AC-765).
                const timer = setTimeout(() => {
                    refetch();
                }, Math.min(delay, MAX_TIMEOUT_DELAY));
                return () => clearTimeout(timer);
            }
        }

        if (!shownRef.current) {
            shownRef.current = true;
            openContactVerify();
        }
    }, [state, isGamePage, refetch, openContactVerify]);

    return null;
}
